import Vue from 'vue'
import axios from "axios"
import api from '../api'

export const state = {
  settings: {
    dateService: {
      serieFactura: '',
      oblioSecretKey: '',
      oblio_email: '',
    },
    tarife: {},
    language: '',
    mechanics_enabled: false,
    mechanics: [],
    statuses: [],
    ramps: [],
  },
  settingsSnapshot: null,
  isLoadingSettings: false,
  // Flips true after the first get_settings attempt resolves. Views that render
  // columns conditionally on settings (e.g. Comenzi's status column) wait for
  // this so the table isn't drawn before settings arrive, then reflowed.
  settingsLoaded: false,
  hasFieldErrors: false,
  conditiiContractHtml: ''
}

export const getters = {
  // Single source of truth for "is SmartBill connected" — consumed by the
  // integrations screen and the main menu (which only shows Facturi when true).
  isSmartBillConnected (state) {
    return Boolean(
      state.settings &&
      state.settings.smartbill_email &&
      state.settings.smartbill_token === true &&
      state.settings.smartbill_series
    )
  },
  // Oblio e simetric cu SmartBill; secret_key vine mascat ca boolean (ca token-ul).
  // Seria e opțională (BE pune default-ul la facturare), deci nu intră în „conectat".
  isOblioConnected (state) {
    return Boolean(
      state.settings &&
      state.settings.oblio_email &&
      state.settings.oblio_secret_key === true
    )
  },
  // Un singur furnizor de facturare e activ la un moment dat (vezi enforce-ul din
  // ecranul de Integrări). Devizul și meniul Facturi consumă aceste două.
  isInvoicingConnected (state, getters) {
    return Boolean(getters.isSmartBillConnected || getters.isOblioConnected)
  },
  activeInvoiceProvider (state, getters) {
    if (getters.isOblioConnected) return 'oblio'
    if (getters.isSmartBillConnected) return 'smartbill'
    return null
  }
}

const RESOURCE_ERROR_MESSAGES = {
  400: 'Completează câmpul înainte de a salva.',
  403: 'Modulul Mecanici nu este activat — salvează activarea mai întâi.',
  404: 'Elementul nu a mai fost găsit — poate a fost șters deja.',
  409: 'Există deja un element cu acest nume.'
}

function resolveResourceError (error) {
  const status = error?.response?.status
  return error?.response?.data?.message || RESOURCE_ERROR_MESSAGES[status] || 'Eroare la salvare. Încearcă din nou.'
}

export const mutations = {
  UPDATE_SETTING (state, { key, value }) {
    Vue.set(state.settings, key, value);
  },
  SET_RAR_FLAGS (state, value) {
    state.settings = {
      ...state.settings,
      rar_email: value,
      rar_password: value
    };
  },
  RESET_RAR_FLAGS (state) {
    state.settings = {
      ...state.settings,
      rar_email: false,
      rar_password: false
    };
  },
  SET_HAYNES_FLAGS (state, value) {
    state.settings = {
      ...state.settings,
      haynes_username: value,
      haynes_password: value
    };
  },
  RESET_HAYNES_FLAGS (state) {
    state.settings = {
      ...state.settings,
      haynes_username: false,
      haynes_password: false
    };
  },
  SET_LOGO (state, value) {
    state.settings = { ...state.settings, logo: value }
  },
  SET_WATERMARK (state, value) {
    state.settings = { ...state.settings, watermark: value }
  },
  SET_SMARTBILL_FLAGS (state, { email, series }) {
    state.settings = {
      ...state.settings,
      smartbill_email: email,
      smartbill_token: true,
      smartbill_series: series
    }
  },
  RESET_SMARTBILL_FLAGS (state) {
    state.settings = {
      ...state.settings,
      smartbill_email: '',
      smartbill_token: false,
      smartbill_series: ''
    }
  },
  SET_OBLIO_FLAGS (state, { email, series }) {
    state.settings = {
      ...state.settings,
      oblio_email: email,
      oblio_secret_key: true,
      oblio_series: series || ''
    }
  },
  RESET_OBLIO_FLAGS (state) {
    state.settings = {
      ...state.settings,
      oblio_email: '',
      oblio_secret_key: false,
      oblio_series: ''
    }
  },
  SET_SETTINGS_SNAPSHOT (state, value) {
    state.settingsSnapshot = value
  },
  SET_SETTINGS_LOADING (state, value) {
    state.isLoadingSettings = value
    // When a load finishes (success or failure), settings are considered
    // resolved — columns that depend on them can render without flicker.
    if (!value) state.settingsLoaded = true
  },
  SET_HAS_FIELD_ERRORS (state, value) {
    state.hasFieldErrors = value
  },
  SET_CONDITII_CONTRACT_HTML (state, value) {
    state.conditiiContractHtml = value
  },
  // actualizeaza un singur camp deja persistat pe server (ex: mechanics/statuses/ramps
  // dupa un create/update/delete dedicat) fara sa calce peste alte campuri nesalvate
  // inca din formular, si tine snapshot-ul sincronizat doar pentru acel camp
  PATCH_SETTINGS_FIELD (state, { key, value }) {
    Vue.set(state.settings, key, value)
    if (state.settingsSnapshot !== null) {
      const snapshot = JSON.parse(state.settingsSnapshot)
      snapshot[key] = value
      state.settingsSnapshot = JSON.stringify(snapshot)
    }
  }
}

export const actions = {
  async get_settings ({state, commit}) {
    commit('SET_SETTINGS_LOADING', true)
    try {
      const { data: setari } = await axios('/api/get-setari');
      localStorage.setItem('language', setari.language);
      // IntrareNoua reads these as plain arrays (`.length`, v-for) without
      // guards, and legacy settings records can omit them entirely.
      if (!setari.mechanics) setari.mechanics = []
      if (!setari.ramps) setari.ramps = []
      if (!setari.statuses) setari.statuses = []
      commit('SET_KEY_VALUE', { key: 'settings', sub: 'setari', value: setari });
      commit('SET_SETTINGS_SNAPSHOT', JSON.stringify(setari));
    } catch (e) {
      state.error = 'Eroare primire comenzi din server.'
    } finally {
      commit('SET_SETTINGS_LOADING', false)
    }
  },
  async save_settings ({ state, commit }) {
    await axios.post('/api/save-settings', state.settings);
    commit('SET_KEY_VALUE', { key: 'lang', value: state.settings.language });
    commit('SET_SETTINGS_SNAPSHOT', JSON.stringify(state.settings));
  },
  async save_rar_settings({ commit }, payload) {
    try {
      const response = await axios.post(api.setari_rar, payload);

      if (response.data && response.data.ok) {
        commit('SET_RAR_FLAGS', true);
      }

      return response.data;

    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
        'Email sau parolă incorectă.'
      );
    }
  },
  async logout_rar ({ state, commit }) {
    try {
      await axios.delete('/api/setari/rar');
      commit('RESET_RAR_FLAGS');
    } catch (e) {
      state.error = 'Eroare logout RAR.'
    }
  },
  async save_haynes_settings({ commit }, payload) {
    try {
      const response = await axios.post(api.setari_haynes, payload);

      if (response.data && response.data.ok) {
        commit('SET_HAYNES_FLAGS', true);
      }

      return response.data;

    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
        'Utilizator sau parolă incorectă.'
      );
    }
  },
  async logout_haynes ({ state, commit }) {
    try {
      await axios.delete(api.setari_haynes);
      commit('RESET_HAYNES_FLAGS');
    } catch (e) {
      state.error = 'Eroare logout Haynes.'
    }
  },
  async upload_logo ({ commit }, file) {
    const formData = new FormData()
    formData.append('logo', file)
    const { data } = await axios.post(api.setari_logo, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    commit('SET_LOGO', data.logo || 'logo.png')
    return data
  },
  async delete_logo ({ commit }) {
    await axios.delete(api.setari_logo)
    commit('SET_LOGO', '')
  },
  async upload_watermark ({ commit }, file) {
    const formData = new FormData()
    formData.append('watermark', file)
    const { data } = await axios.post(api.setari_watermark, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    commit('SET_WATERMARK', data.watermark || 'watermark.png')
    return data
  },
  async delete_watermark ({ commit }) {
    await axios.delete(api.setari_watermark)
    commit('SET_WATERMARK', '')
  },
  async save_smartbill_settings ({ commit }, payload) {
    try {
      const response = await axios.post(api.setari_smartbill, payload)
      if (response.data && response.data.ok) {
        // Payload-ul de conectare poartă credențialele; update-ul de gestiune
        // (cât ești deja conectat) trimite doar smartbill_warehouse, BE folosind
        // token-ul salvat în DB. Reflectăm fiecare parte doar dacă e prezentă.
        if (payload.smartbill_email) {
          commit('SET_SMARTBILL_FLAGS', {
            email: payload.smartbill_email,
            series: payload.smartbill_series
          })
        }
        if ('smartbill_warehouse' in payload) {
          commit('UPDATE_SETTING', { key: 'smartbill_warehouse', value: payload.smartbill_warehouse })
        }
      }
      return response.data
    } catch (error) {
      // BE-ul trimite mesajul pe câmpul `error` (ex: „...sunt obligatorii",
      // „Gestiunea 'X' nu a fost găsită"); `message` e păstrat ca fallback.
      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Credențiale invalide.'
      )
    }
  },
  async delete_smartbill_settings ({ state, commit }) {
    try {
      await axios.delete(api.setari_smartbill)
      commit('RESET_SMARTBILL_FLAGS')
    } catch (e) {
      state.error = 'Eroare deconectare SmartBill.'
    }
  },
  async save_oblio_settings ({ commit }, payload) {
    try {
      const response = await axios.post(api.setari_oblio, payload)
      if (response.data && response.data.ok && payload.oblio_email) {
        commit('SET_OBLIO_FLAGS', {
          email: payload.oblio_email,
          series: payload.oblio_series
        })
      }
      return response.data
    } catch (error) {
      // BE trimite mesajul pe `error` (ex: „CUI neconfigurat", „serie inexistentă");
      // `message` e fallback.
      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Credențiale invalide.'
      )
    }
  },
  async delete_oblio_settings ({ state, commit }) {
    try {
      await axios.delete(api.setari_oblio)
      commit('RESET_OBLIO_FLAGS')
    } catch (e) {
      state.error = 'Eroare deconectare Oblio.'
    }
  },
  async create_mechanic ({ commit }, payload) {
    try {
      const { data } = await axios.post(api.setari_mechanics, payload)
      commit('PATCH_SETTINGS_FIELD', { key: 'mechanics', value: data.mechanics })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async update_mechanic ({ commit }, { id, name }) {
    try {
      const { data } = await axios.put(`${api.setari_mechanics}/${id}`, { name })
      commit('PATCH_SETTINGS_FIELD', { key: 'mechanics', value: data.mechanics })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async delete_mechanic ({ commit }, id) {
    try {
      const { data } = await axios.delete(`${api.setari_mechanics}/${id}`)
      commit('PATCH_SETTINGS_FIELD', { key: 'mechanics', value: data.mechanics })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async create_status ({ commit }, payload) {
    try {
      const { data } = await axios.post(api.setari_statuses, payload)
      commit('PATCH_SETTINGS_FIELD', { key: 'statuses', value: data.statuses })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async update_status ({ commit }, { id, label, color }) {
    try {
      const { data } = await axios.put(`${api.setari_statuses}/${id}`, { label, color })
      commit('PATCH_SETTINGS_FIELD', { key: 'statuses', value: data.statuses })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async delete_status ({ commit }, id) {
    try {
      const { data } = await axios.delete(`${api.setari_statuses}/${id}`)
      commit('PATCH_SETTINGS_FIELD', { key: 'statuses', value: data.statuses })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async create_ramp ({ commit }, payload) {
    try {
      const { data } = await axios.post(api.setari_ramps, payload)
      commit('PATCH_SETTINGS_FIELD', { key: 'ramps', value: data.ramps })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async update_ramp ({ commit }, { id, label }) {
    try {
      const { data } = await axios.put(`${api.setari_ramps}/${id}`, { label })
      commit('PATCH_SETTINGS_FIELD', { key: 'ramps', value: data.ramps })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async delete_ramp ({ commit }, id) {
    try {
      const { data } = await axios.delete(`${api.setari_ramps}/${id}`)
      commit('PATCH_SETTINGS_FIELD', { key: 'ramps', value: data.ramps })
      return data
    } catch (error) {
      throw new Error(resolveResourceError(error))
    }
  },
  async fetch_conditii_contract ({ state, commit }) {
    if (state.conditiiContractHtml) return
    try {
      const { data } = await axios.get(api.conditii_contract)
      commit('SET_CONDITII_CONTRACT_HTML', (data && data.html) || '')
    } catch (e) {
      // Fragmentul lipsă nu trebuie să blocheze printarea; rămâne ''.
    }
  },
  async get_reports (_, { start, end }) {
    try {
      const { data } = await axios.get(api.reports, { params: { start, end } })
      return data
    } catch (error) {
      const status = error?.response?.status
      const message = error?.response?.data?.message || (
        status === 400 ? 'Selectează un interval de date valid.' :
        status === 403 ? 'Modulul Mecanici nu este activat.' :
        'Eroare la încărcarea raportului. Încearcă din nou.'
      )
      throw new Error(message)
    }
  }
}
